// Custom cookie consent (US opt-out model), ported from forzabuilt-website's src/lib/consent.ts.
// Shared by the banner/modal (components/consent/CookieConsentManager.jsx), the footer and
// legal pages. The bootstrap that loads tracker scripts before React is public/js/consent.js;
// everything that writes or logs a consent record lives here, so the policy version and the
// audit-log payload have a single source.

export const CONSENT_STORAGE_KEY = 'ruggedred_consent_preferences';
export const CONSENT_VERSION = 2;
/** Bump when the cookie policy materially changes; stored with each consent record. */
export const POLICY_VERSION = '2026-10';

export const CONSENT_UPDATED_EVENT = 'ruggedred:consent-updated';
export const OPEN_CONSENT_PREFERENCES_EVENT = 'ruggedred:open-consent-preferences';

// Vercel serverless function in frontend/api/consent-log.js.
export const CONSENT_LOG_ENDPOINT = '/api/consent-log';

/** @typedef {'banner' | 'modal' | 'footer-link' | 'do-not-sell' | 'gpc' | 'unknown'} ConsentSource */

/**
 * @typedef {Object} ConsentPreferences
 * @property {number} version
 * @property {string} id Random UUID with no link to any account/email; used only to correlate audit-log entries.
 * @property {string} timestamp
 * @property {ConsentSource} source
 * @property {string} policyVersion
 * @property {true} essential
 * @property {boolean} analytics Vercel Analytics
 * @property {boolean} marketing Advertising / audience tech (none in use today)
 */

const isBrowser = () => typeof window !== 'undefined';

const generateConsentId = () => {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
  } catch {
    // fall through
  }
  // Fallback UUIDv4-ish
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

// public/js/consent.js repeats this check (it runs before the bundle); keep the two in step.
const isValidConsentRecord = (record) =>
  !!record &&
  typeof record === 'object' &&
  record.version === CONSENT_VERSION &&
  typeof record.id === 'string' &&
  typeof record.timestamp === 'string' &&
  typeof record.policyVersion === 'string' &&
  record.essential === true &&
  typeof record.analytics === 'boolean' &&
  typeof record.marketing === 'boolean';

const persist = (record) => {
  try {
    window.localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(record));
    return true;
  } catch {
    return false;
  }
};

// In-memory copy of the stored record, so hot paths (Vercel Analytics beforeSend) don't
// re-read and re-parse localStorage. Kept current by writeConsent and the cross-tab
// `storage` listener below. `undefined` means not loaded yet.
let currentConsent;

/** The current consent record (cached), or null when the visitor hasn't decided. */
export const getCurrentConsent = () => {
  if (currentConsent === undefined) currentConsent = readConsent();
  return currentConsent;
};

/** True when the browser sends a Global Privacy Control signal. */
export const gpcEnabled = () =>
  typeof navigator !== 'undefined' && navigator.globalPrivacyControl === true;

/** @returns {ConsentPreferences | null} */
export const readConsent = () => {
  if (!isBrowser()) return null;

  try {
    const raw = window.localStorage.getItem(CONSENT_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return isValidConsentRecord(parsed) ? parsed : null;
  } catch {
    return null;
  }
};

/**
 * @param {{ analytics: boolean, marketing: boolean }} choices
 * @param {ConsentSource} source
 * @returns {ConsentPreferences | null}
 */
export const writeConsent = (choices, source) => {
  if (!isBrowser()) return null;

  const existing = readConsent();
  const next = {
    version: CONSENT_VERSION,
    id: existing?.id ?? generateConsentId(),
    timestamp: new Date().toISOString(),
    source,
    policyVersion: POLICY_VERSION,
    essential: true,
    analytics: choices.analytics,
    marketing: choices.marketing,
  };

  if (!persist(next)) return null;
  currentConsent = next;
  return next;
};

/** US opt-out model: allowed unless the user has explicitly opted out. */
export const canLoadAnalytics = (consent) => (consent ? consent.analytics : true);

export const canLoadMarketing = (consent) => (consent ? consent.marketing : true);

/** True only when the visitor has explicitly opted out of analytics. Used to gate Vercel Analytics. */
export const analyticsOptedOut = () => !canLoadAnalytics(getCurrentConsent());

export const emitConsentUpdated = (consent) => {
  if (!isBrowser()) return;
  window.dispatchEvent(new CustomEvent(CONSENT_UPDATED_EVENT, { detail: consent }));
};

/**
 * Opens the preferences modal. `source` records which entry point was used
 * (e.g. 'footer-link', 'do-not-sell') so that if the user saves from this
 * session, the audit log reflects the actual compliance-relevant flow rather
 * than always logging a generic 'modal' save.
 */
export const emitOpenConsentPreferences = (source) => {
  if (!isBrowser()) return;
  window.dispatchEvent(new CustomEvent(OPEN_CONSENT_PREFERENCES_EVENT, { detail: { source } }));
};

/**
 * Fire-and-forget anonymous consent log for compliance/audit.
 * Sends only the consent record (random UUID, choices, timestamps) — no PII.
 * `gpc` records whether the browser was sending a GPC signal at the time.
 */
export const sendConsentLog = (consent, gpc = gpcEnabled()) => {
  if (!isBrowser()) return;

  const payload = JSON.stringify({
    consentId: consent.id,
    occurredAt: consent.timestamp,
    consentVersion: consent.version,
    policyVersion: consent.policyVersion,
    source: consent.source,
    analytics: consent.analytics,
    marketing: consent.marketing,
    gpc,
  });

  try {
    if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
      const blob = new Blob([payload], { type: 'application/json' });
      if (navigator.sendBeacon(CONSENT_LOG_ENDPOINT, blob)) return;
    }
  } catch {
    // fall through to fetch
  }

  void fetch(CONSENT_LOG_ENDPOINT, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: payload,
    keepalive: true,
  }).catch(() => {
    // Logging must never break the UX.
  });
};

/**
 * Persists and logs the consent record a choice produces, and tells the tracker bootstrap
 * and any open UI about it. The one path every explicit decision goes through.
 */
export const saveConsent = (choices, source) => {
  const next = writeConsent(choices, source);
  if (!next) return null;
  emitConsentUpdated(next);
  sendConsentLog(next);
  return next;
};

if (isBrowser()) {
  // First visit with GPC: record and log an Advertising opt-out before anything renders.
  // public/js/consent.js has already applied the same choice to trackers in memory.
  if (!getCurrentConsent() && gpcEnabled()) {
    saveConsent({ analytics: true, marketing: false }, 'gpc');
  }

  // A choice saved in another tab: refresh the cache and re-apply it here, so trackers in
  // this tab follow it and a later save from this tab starts from it rather than overwriting it.
  window.addEventListener('storage', (event) => {
    if (event.key !== null && event.key !== CONSENT_STORAGE_KEY) return;
    currentConsent = readConsent();
    if (currentConsent) emitConsentUpdated(currentConsent);
  });
}
